import { useState, useRef } from 'react';
import { Crown, Award, CheckCircle, Download, Linkedin, Share2, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const Certificate = ({ 
  studentName, 
  courseName, 
  courseId,
  completionDate, 
  certificateId,
  onClose 
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const certificateRef = useRef(null);

  const formattedDate = new Date(completionDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const downloadPDF = async () => {
    if (!certificateRef.current) return;
    
    setIsGenerating(true);
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#0D0D0D',
        logging: false
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Queen_B_Academy_Certificate_${courseId}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const shareOnLinkedIn = () => {
    const text = encodeURIComponent(
      `I just completed the ${courseName} at Queen B Academy! 🎓\n\n` +
      `Certificate ID: ${certificateId}\n\n` +
      `#QueenBLogistics #FreightIndustry #ProfessionalDevelopment #Trucking`
    );
    const url = encodeURIComponent(`https://queenblogistics.com/academy`);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}&text=${text}`,
      '_blank',
      'width=600,height=600'
    );
  };

  const copyVerificationLink = () => {
    const link = `https://queenblogistics.com/verify/${certificateId}`;
    navigator.clipboard.writeText(link);
    alert('Verification link copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 overflow-y-auto" data-testid="certificate-modal">
      <div className="relative w-full max-w-5xl">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors"
          data-testid="certificate-close-btn"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Certificate */}
        <div 
          ref={certificateRef}
          className="relative bg-[#0D0D0D] border-4 border-[#D4AF37] p-8 md:p-12"
          style={{ 
            aspectRatio: '1.414/1',
            background: 'linear-gradient(135deg, #0D0D0D 0%, #1a1a1a 50%, #0D0D0D 100%)'
          }}
        >
          {/* Corner Accents */}
          <div className="absolute top-4 left-4 w-12 md:w-16 h-12 md:h-16 border-t-2 border-l-2 border-[#D4AF37]/50" />
          <div className="absolute top-4 right-4 w-12 md:w-16 h-12 md:h-16 border-t-2 border-r-2 border-[#D4AF37]/50" />
          <div className="absolute bottom-4 left-4 w-12 md:w-16 h-12 md:h-16 border-b-2 border-l-2 border-[#D4AF37]/50" />
          <div className="absolute bottom-4 right-4 w-12 md:w-16 h-12 md:h-16 border-b-2 border-r-2 border-[#D4AF37]/50" />

          {/* Inner Border */}
          <div className="absolute inset-6 md:inset-8 border border-[#D4AF37]/20" />

          {/* Content */}
          <div className="relative z-10 h-full flex flex-col items-center justify-between text-center py-2 md:py-4">
            
            {/* Logo & Header */}
            <div>
              <div className="flex items-center justify-center gap-2 md:gap-3 mb-2 md:mb-4">
                <Crown className="w-6 h-6 md:w-10 md:h-10 text-[#D4AF37]" />
                <span className="font-['Barlow_Condensed'] text-xl md:text-3xl font-bold text-[#D4AF37] uppercase tracking-wider">
                  Queen B Academy
                </span>
                <Crown className="w-6 h-6 md:w-10 md:h-10 text-[#D4AF37]" />
              </div>
              <p className="text-white/40 text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.3em]">Certificate of Completion</p>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col items-center justify-center py-4 md:py-8">
              <p className="text-white/60 text-sm md:text-lg mb-1 md:mb-2">This certifies that</p>
              
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-white mb-2 md:mb-4 tracking-wide">
                {studentName}
              </h2>
              
              <p className="text-white/60 text-sm md:text-lg mb-4 md:mb-6">has successfully completed the</p>
              
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 md:px-8 py-2 md:py-4 mb-4 md:mb-6">
                <h3 className="font-['Barlow_Condensed'] text-xl md:text-3xl font-bold text-[#D4AF37] uppercase">
                  {courseName}
                </h3>
              </div>

              <div className="flex items-center gap-2 text-white/50 text-xs md:text-base">
                <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[#D4AF37]" />
                <span>All modules completed with demonstrated proficiency</span>
              </div>
            </div>

            {/* Footer */}
            <div className="w-full">
              <div className="flex items-center justify-between px-2 md:px-8">
                {/* Date */}
                <div className="text-left">
                  <p className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider mb-1">Date Issued</p>
                  <p className="text-white font-medium text-xs md:text-base">{formattedDate}</p>
                </div>

                {/* Seal */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 md:w-20 md:h-20 rounded-full border-2 border-[#D4AF37] flex items-center justify-center bg-[#D4AF37]/5">
                    <Award className="w-7 h-7 md:w-10 md:h-10 text-[#D4AF37]" />
                  </div>
                  <p className="text-[#D4AF37] text-[10px] md:text-xs mt-1 md:mt-2 font-medium">VERIFIED</p>
                </div>

                {/* Certificate ID */}
                <div className="text-right">
                  <p className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider mb-1">Certificate ID</p>
                  <p className="text-white font-mono text-[10px] md:text-sm">{certificateId}</p>
                </div>
              </div>

              {/* Signature Line */}
              <div className="mt-4 md:mt-6 pt-2 md:pt-4 border-t border-white/10">
                <p className="text-[#D4AF37] font-['Barlow_Condensed'] text-lg md:text-xl italic">Queen B Logistics</p>
                <p className="text-white/40 text-[10px] md:text-xs uppercase tracking-wider">Premium Freight Operations</p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
          <button 
            onClick={downloadPDF}
            disabled={isGenerating}
            className="w-full sm:w-auto px-6 py-3 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="download-pdf-btn"
          >
            {isGenerating ? (
              <>
                <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download PDF
              </>
            )}
          </button>
          
          <button 
            onClick={shareOnLinkedIn}
            className="w-full sm:w-auto px-6 py-3 bg-[#0077B5] text-white font-bold uppercase tracking-wider hover:bg-[#005582] transition-colors flex items-center justify-center gap-2"
            data-testid="linkedin-share-btn"
          >
            <Linkedin className="w-5 h-5" />
            Share on LinkedIn
          </button>
          
          <button 
            onClick={() => setShowShareOptions(!showShareOptions)}
            className="w-full sm:w-auto px-6 py-3 border border-white/20 text-white font-bold uppercase tracking-wider hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
            data-testid="more-options-btn"
          >
            <Share2 className="w-5 h-5" />
            More Options
          </button>
        </div>

        {/* Share Options Dropdown */}
        {showShareOptions && (
          <div className="mt-4 p-4 bg-[#121212] border border-white/10 text-center" data-testid="share-options-panel">
            <p className="text-white/60 text-sm mb-3">Share your achievement</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button 
                onClick={copyVerificationLink}
                className="px-4 py-2 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                data-testid="copy-verification-link-btn"
              >
                Copy Verification Link
              </button>
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I just completed ${courseName} at Queen B Academy! 🎓 #QueenBLogistics #FreightIndustry`)}&url=${encodeURIComponent('https://queenblogistics.com/academy')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-white/10 text-white text-sm hover:bg-white/20 transition-colors"
                data-testid="twitter-share-link"
              >
                Share on X (Twitter)
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certificate;
